import { useClickAway, useRequest } from 'ahooks'
import { message } from 'antd'
import { useAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { ProjectStage } from '@/schemas'

import { stagesAtom } from './stages.atom'

interface StageEditProps {
  record: ProjectStage
  renderData: (record: ProjectStage) => React.ReactElement
  renderEdit?: (record: ProjectStage, onChange: any) => React.ReactElement
  onSuccess: () => void
}

export const StageEdit: React.FC<StageEditProps> = ({
  children,
  record,
  renderData,
  renderEdit,
  onSuccess,
  ...restProps
}) => {
  const { i18n } = useTranslation()
  const ref = React.useRef<HTMLTableCellElement | null>(null)
  const [isEditing, setIsEditing] = React.useState(false)
  const [state, setState] = React.useState<ProjectStage>(record)
  const [stages] = useAtom(stagesAtom)

  const { run } = useRequest(
    () => ({
      url: '/api/project/updateProjectStage',
      method: 'post',
      data: {
        ...state,
        selfId: record.selfId,
        id: record.id,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        onSuccess()
        setIsEditing(false)
      },
    }
  )

  React.useEffect(() => {
    setState(record)
  }, [record])

  const isPopover = (el: HTMLElement | null) => {
    while (el) {
      if (
        ['ant-picker-dropdown', 'ant-select-dropdown', 'ant-popover', 'ant-picker-header', 'ant-picker-content'].find(
          (c) => el?.classList.contains(c)
        )
      ) {
        return true
      }
      el = el.parentElement
    }
    return false
  }

  useClickAway((e) => {
    if (isPopover(e.target as HTMLElement)) {
      return
    }

    if (state) {
      if (state['error']) {
        message.error(state['error'])
        return
      }
      run()
    } else if (isEditing) {
      setIsEditing(false)
    }
  }, ref)

  const onUpdate = (changes: any) => {
    if (changes.name) {
      const stage = stages.find((s) => s.ename === changes.name || s.name === changes.name)
      if (stage) {
        setState((state) => ({
          ...state,
          name: i18n.resolvedLanguage === 'zh' ? stage.name : stage.ename,
          error: changes.error,
          stageId: stage.id,
        }))
      } else {
        setState((state) => ({ ...state, name: changes.name, error: changes.error, stageId: null }))
      }
    } else {
      setState((state) => ({ ...state, ...changes }))
    }
  }

  if (!renderData) {
    return <td {...restProps}>{children}</td>
  }

  return (
    <td
      {...restProps}
      className={`ant-table-cell ${renderEdit ? 'cursor-pointer' : 'cursor-default'}`}
      ref={isEditing ? ref : null}
      onKeyPress={(e) => {
        if (e.nativeEvent.key === 'Enter') {
          // 成员选择器回车会先触发 onUpdate, 等 onUpdate 更新完 state 再保存
          window.setTimeout(() => run(), 0)
        }
      }}
      onKeyDown={(e) => {
        if (e.nativeEvent.key === 'Escape') {
          setState(record)
          setIsEditing(false)
        }
      }}
      onClick={(e) => {
        if (!!renderEdit && !isEditing) {
          if (isPopover(e.target as HTMLElement)) {
            return
          }
          e.stopPropagation()
          setIsEditing(true)
        }
      }}
    >
      {!!renderEdit && isEditing ? renderEdit(state, onUpdate) : renderData(state)}
    </td>
  )
}
